/* eslint-disable jsdoc/no-undefined-types */
import get from 'lodash.get';

import cartData from '#shared/util/cartData.js';
import isObject from '#shared/util/isObject.js';
import openCartMiddleware from '#shared/middleware/openCartMiddleware.js';

import cAcceptedPaymentMethods from '#themes/default/default/components/acceptedPaymentMethods/acceptedPaymentMethods.js';
import cBillingFormBrazil from '#themes/default/default/components/billingFormBrazil/billingFormBrazil.js';
import cButton from '#themes/default/default/components/button/button.js';
import cButtons from '#themes/default/default/components/buttons/buttons.js';
import cFormInput from '#themes/default/default/components/formInput/formInput.js';
import cFormLine from '#themes/default/default/components/formLine/formLine.js';
import cInput from '#themes/default/default/components/input/input.js';
import cInputGroup from '#themes/default/default/components/inputGroup/inputGroup.js';
import cSelect from '#themes/default/default/components/select/select.js';

import isValid from '#frontend/util/isValid.js';
import focusFirstError from '#frontend/util/focusFirstError.js';

import template from './shoppingCart.html' with { type: 'html' };

/**
 * @memberof ShoppingCartPage
 * @feature Extended product descriptions (with customID DESCON) [GEN]
 * @feature Variant selector if variants are available (with customID VSON) [GEN]
 * @feature Quantity selector if enabled for the product [GEN]
 * @feature Prices with/without VAT based on locale [GEN]
 * @feature US/CA zip validation [GEN]
 * @feature Display monthly pricing (with customID PPMONTH) [GEN]
 * @feature Street prices and list prices (if discount is available) [GEN]
 * @feature Offer modal - cross-sell (upsell with customID UPON) [GEN]
 * @feature Offer modal - upsell consumer to business product []
 * @feature Disable offer modal (with customID CPNO) [GEN]
 * @feature Move PayPal to the first position (with customID PAYPALFIRST) [GEN]
 * @feature Display all available payment methods (with customID PMSHOW) [GEN]
 * @feature Display phone number field (with customID PHONESHOW) [GEN]
 * @feature Existing email/domain check [GEN]
 * @feature Email suggestion (gmail.com typos) [GEN]
 * @feature Disable country selector (with customID COUNTRYOFF) [GEN]
 * @feature Individual/company switch (only for consumer products) [GEN]
 * @feature Show security code note/icon on input focus [GEN]
 * @feature Offline note visible when offline method is selected [GEN]
 * @feature 1-click (authenticated) flow [GEN]
 * @feature Disabled 1-click (authenticated) flow (with customID 1CLICKOFF) [GEN]
 * @feature Disabled PayPal in 1-click (authenticated) flow (with customID 1CLICKPAYPALOFF) [GEN]
 * @feature Close offer - after app window closing [GEN]
 * @feature Disable close offer (with customID CLOSEOFFERNO) [GEN]
 * @feature Pre-selection flow (Trial products only) [GEN]
 */
export default {
	...cartData,
	middleware: [
		openCartMiddleware
	],
	components: {
		cAcceptedPaymentMethods,
		cBillingFormBrazil,
		cButton,
		cButtons,
		cFormInput,
		cFormLine,
		cInput,
		cInputGroup,
		cSelect
	},
	meta() {
		return {
			title: `${this.t('global_metaTitle')} - ${this.t('cart_metaTitle')}`
		};
	},
	data() {
		return {
			formValid: false,
			firstProduct: this.order.products[0] || null,
			paymentSelectionEventFired: false,
			brazilianPaymentMethods: [{
				title: 'Boleto',
				svgPath: '#payment-boleto-icon'
			}, {
				title: 'Mastercard',
				svgPath: '#payment-mastercard'
			}, {
				title: 'Visa',
				svgPath: '#payment-visa'
			}, {
				title: 'Elo',
				svgPath: '#payment-elo'
			}, {
				title: 'Hipercard',
				svgPath: '#payment-hipercard'
			}]
		};
	},
	computed: {
		getHeadline() {
			if (this.isNewMethod) {
				return this.t('shoppingCart_headlineAddPayment');
			} else if (!this.order.isGuest) {
				return this.t('shoppingCart_headlineConfirm');
			} else if (this.order.hasTrialProduct && this.isNewCart) {
				return this.t('shoppingCart_headlinePreselection');
			} else {
				return this.t('shoppingCart_headline');
			}
		},
		isCreditCard() {
			return isObject(this.selectedPayment) && this.selectedPayment.type === 'creditCard';
		},
		isCreditCardTouched() {
			return isObject(this.form) && this.form.creditCard?.touched;
		},
		isPayPalBilling() {
			return isObject(this.selectedPayment) && this.selectedPayment.type === 'payPalBilling';
		},
		isIDeal() {
			return isObject(this.selectedPayment) && this.selectedPayment.type === 'iDeal';
		},
		isDirectDebit() {
			return isObject(this.selectedPayment) && this.selectedPayment.type === 'directDebit';
		},
		isDirectDebitTouched() {
			return isObject(this.form) && this.form.directDebit?.touched;
		},
		isBancontact() {
			return isObject(this.selectedPayment) && this.selectedPayment.type === 'bancontact';
		},
		isBancontactTouched() {
			return isObject(this.form) && this.form.bancontact?.touched;
		},
		isBrazilianFlow() {
			return this.order?.isBrazilianFlow;
		},
		getPageName() {
			if (this.isPreselection) {
				return 'Payment Selector';
			} else if (!this.order.isGuest && !this.isNewMethod) {
				return '1-click';
			} else {
				return this.page.tracking;
			}
		},
		getSubmitText() {
			if (!this.isCustomId['CART-4550'] && !this.order.isGuest && !this.isNewMethod) { // One-click
				return this.t('cart_submit');
			} else if (this.order.isGuest && this.isCompany && this.selectedPayment.isCompany) { // Buying for a company (guest)
				return this.t('cart_continueCompany');
			} else if (this.isPayPalBilling) { // PayPal
				return this.t('cart_continuePayPal');
			}
			return this.t('cart_continue'); // Guest / Add new method
		},
		getSubmitPath() {
			if (!this.isCustomId['CART-4550'] && !this.order.isGuest && !this.isNewMethod) { // One-click
				return 'thank-you';
			} else if (this.order.isGuest && this.isCompany && this.selectedPayment.isCompany && !this.isBrazilianFlow) { // Buying for a company (guest)
				return 'company-details';
			} else if (this.isBrazilianFlow) {
				return 'payment-details';
			}
			return 'confirm-order'; // Guest / Add new method
		},
		zipCodeState() {
			return this.$store.state.zipCodeState;
		},
		areTermsAccepted() {
			return this.$store.getters.areTermsAccepted;
		},
		hasTermsCheckbox() {
			return this.$store.getters.hasTermsCheckbox;
		},
		isPreselection() {
			return this.order.hasTrialProduct && this.isNewCart && this.order.isGuest && !this.order.isBrazilianFlow;
		},
		showDisclaimer() {
			return this.$store.getters.showDisclaimer;
		},
		showAdditionalInfo() {
			return this.isDirectDebit || this.order.isBrazilianFlow;
		},
		trackingObject() {
			let stepNumber = this.page.number;
			// "Add payment method" page (one-click)
			if (this.isNewMethod && !this.order.isGuest) {
				stepNumber = 1;
			}
			// "Trial payment selector" - after PM was already selected
			if (this.order.hasTrialProduct && !this.isNewCart) {
				stepNumber = 6;
			}
			return {
				app: this.app,
				url: this.url,
				page: {
					...this.page,
					tracking: this.getPageName,
					number: stepNumber
				},
				order: this.order,
				selectedPayment: this.selectedPayment,
				theme: this.theme
			};
		},
		providerElementsLoaded() {
			return this.$store.getters.providerElementsLoaded;
		},
		hasPaymentOptions() {
			const paymentOptions = this.payment.providerAccountPaymentOptions;
			if (this.order.hasTrialProduct && this.isNewMethod) {
				return paymentOptions.filter((paymentOption) => paymentOption.type !== 'payPalBilling').length ?? null;
			} else {
				return paymentOptions;
			}
		}
	},
	watch: {
		isNewMethod() {
			if (!this.order.isGuest) {
				this.tracking.checkout(this.trackingObject);
			}
		},
		payment(value) {
			if (!this.order.isGuest && !this.isNewMethod && value.failed) { // Track page 0 if payment fails in one-click
				this.tracking.checkout(this.trackingObject);
			}
		}
	},
	created() {
		this.utils.showLoader();
	},
	async mounted() {
		if (this.payment.failed) {
			this.utils.hideLoader();
			this.tracking.userData({
				account: this.account,
				theme: this.theme
			});
			this.utils.unsetPaymentFailed();
			this.$store.commit('SET_MODAL', 'paymentFailed');
		}
		await this.waitUntil([() => !this.isEmpty], 60 * 1000);
		this.utils.recomputeOfferProperty();
		if (!this.isEmpty) {
			this.tracking.cartOpen(this.trackingObject);
			this.tracking.checkout(this.trackingObject);
		}
		this.utils.hideLoader();
	},
	methods: {
		async handleCheckoutFormSubmit() {
			// Present loading state to the user
			this.utils.showLoader();
			// Remove payment failures if any
			this.utils.unsetPaymentFailed();
			// Determine the actual flow which will then select the corresponding cart submit method
			const useAuthenticatedFlow = !this.order.isGuest && !this.isNewMethod;
			// Submit the order depending on the flow and return the result
			const continueOrRedirect = useAuthenticatedFlow ? await this.startAuthenticatedSubmitFlow() : await this.startGuestSubmitFlow();
			// If response is URL address, we will redirect (for example PayPal gateway), otherwise we will continue in the app
			if (/^http/.test(continueOrRedirect)) {
				location.href = continueOrRedirect;
			} else if (continueOrRedirect) {
				await this.$router.push(this.getLink(this.getSubmitPath)).catch(() => {});
			}
		},
		showPaymentFailedModal() {
			const cvcElement = this.selectedPayment.cardcvv;
			this.utils.setPaymentFailed();
			this.utils.hideLoader();
			this.$store.commit('SET_MODAL', 'paymentFailed');
			if (cvcElement) {
				cvcElement.clear();
			}
		},
		cancelAddPaymentMethod() {
			this.utils.showLoader();
			this.$store.commit('SET_NEW_METHOD', false);
			// Load HPP after setting new method to get correct styles
			this.providersLibrary.loadPayment();
			this.$store.commit('SET_SELECTED_PAYMENT', {
				cardcvv: null,
				cardexpiration: null,
				cardnumber: null,
				iban: null
			});
			this.utils.hideLoader();
		},
		validateForm() {
			this.formValid = isValid('form');
		},
		focusInvalidCCField() {
			const creditCardFields = get(this.paymentContainer, '$refs.creditCardPaymentMethod.$refs');
			focusFirstError(creditCardFields, this.theme?.provider.name);
		},
		focusInvalidIBANField() {
			const directDebitFields = get(this.paymentContainer, '$refs.directDebitPaymentMethod.$refs');
			focusFirstError(directDebitFields, this.theme?.provider.name);
		},
		focusInvalidBancontactField() {
			const bancontacttFields = get(this.paymentContainer, '$refs.bancontactPaymentMethod.$refs');
			focusFirstError(bancontacttFields, this.theme?.provider.name);
		},
		focusTermsCheckbox() {
			const termsCheckbox = document.querySelector('.legalAgreementCheckbox .checkbox_button');
			// Focus T&C checkbox on button click
			if (termsCheckbox) {
				termsCheckbox.focus();
			}
			// Set terms status to seen = visibly different styling for checkbox + text
			this.$store.commit('SET_TERMS_AGREEMENT', {
				checkbox: true,
				seen: true
			});
		},
		trackPaymentSelection() {
			if (!this.paymentSelectionEventFired) {
				this.tracking.paymentSelected({
					order: this.order
				}, this.selectedPayment);
				this.paymentSelectionEventFired = true;
			}
		},
		async storeInfoSubmitAttempt(isSelected, isTouched, paymentMethod = '', focusInvalidMethod = () => {}) {
			// Store information about PM submit attempt
			if (isSelected) {
				this.$store.commit('SET_FORM', {
					[paymentMethod]: {
						submitted: true
					}
				});
				// Do not continue if PM hasn't been filled or interacted with
				if (!isTouched) {
					await this.$nextTick();
					focusInvalidMethod();
					this.utils.hideLoader();
					throw new Error('Customer hasnt interacted with PM iframe yet but tried to submit the order');
				}
			}
		},
		async startAuthenticatedSubmitFlow() {
			// Track payment selection
			this.trackPaymentSelection();
			// Validate customer fields
			this.validateForm();
			// Store information about Credit Card submit attempt
			if (this.isCreditCard) {
				this.$store.commit('SET_FORM', {
					creditCard: {
						submitted: true
					}
				});
				// Do not continue if Credit Card hasn't been filled or interacted with
				if (!this.isCreditCardTouched) {
					await this.$nextTick();
					this.focusInvalidCCField();
					this.utils.hideLoader();
					return false;
				}
			}
			// If customer billing form is invalid or creditCard hasn't been filled/interacted, cancel the order submit
			if (!this.formValid) {
				this.utils.hideLoader();
				return false;
			}
			// Authenticate credit card payment source or mark as authenticated for other payment methods
			const authenticatedPaymentSource = await this.providersLibrary.authenticateSource();
			// If no authenticated source was returned, fail the order submit
			if (!authenticatedPaymentSource) {
				this.showPaymentFailedModal();
				return false;
			}
			// Apply authenticated payment source so all details are set (eg. payment gateway pages, updated sourceId)
			await this.utils.applyPayment(authenticatedPaymentSource);
			// Show the offer modal window
			if (this.isCustomId['CART-4550'] && this.shouldGetOffer) {
				this.utils.hideLoader();
				this.utils.showOfferModal();
				// Set all states to true, so the redirect won't be blocked by untouched CC validation
				this.$store.commit('SET_FORM', {
					creditCard: {
						completed: true,
						submitted: true,
						touched: true
					}
				});
				return false;
			}
			// CART-4550 - Don't submit the order, but redirect to the next page
			if (this.isCustomId['CART-4550']) {
				return true;
			}
			// Use the payment option applied in the cart and submit the order
			try {
				const submitOrderResponse = await this.utils.submitOrder();
				const continueToNextStep = Boolean(submitOrderResponse);
				return continueToNextStep;
			} catch {
				this.showPaymentFailedModal();
				return false;
			}
		},
		async startGuestSubmitFlow() {
			// Track payment selection
			this.trackPaymentSelection();
			// Store payment details for future use (otherwise overwritten by applyBilling call)
			const { bank } = this.payment;
			// If selected payment method is not enabled for the company flow, reset the company fields
			if (!this.selectedPayment.isCompany) {
				this.$store.commit('SET_COMPANY', false);
				await this.$nextTick();
			}
			// Reset all company fields if we are not submitting the company order
			if (!this.isCompany && this.customer.billing.companyName) {
				const addressLine = this.customer.billing.line1;
				await this.utils.applyCompany({
					companyName: '',
					taxId: '',
					line1: '',
					line2: ''
				});
				// Remember address for flow with an address field on order page
				if (this.order.displayAddressField) {
					this.$store.commit('SET_CUSTOMER', {
						...this.customer,
						billing: {
							...this.customer.billing,
							line1: addressLine
						}
					});
				}
			}
			// Wait until state was filled by the ZIP code
			await this.waitUntil([() => this.zipCodeState === 'done']);
			// Validate customer fields
			this.validateForm();
			if (!this.isBrazilianFlow) {
				try {
					// Store information about Credit Card submit attempt
					await this.storeInfoSubmitAttempt(this.isCreditCard, this.isCreditCardTouched, 'creditCard', this.focusInvalidCCField);
					// Store information about DirectDebit submit attempt
					await this.storeInfoSubmitAttempt(this.isDirectDebit, this.isDirectDebitTouched, 'directDebit', this.focusInvalidIBANField);
					// Store information about Bancontact submit attempt
					await this.storeInfoSubmitAttempt(this.isBancontact, this.isBancontactTouched, 'bancontact', this.focusInvalidBancontactField);
				} catch {
					// Cancel the submit attempt if the customer didn't interacted with the iframe
					return false;
				}
			}
			let paymentSource;
			if (this.formValid) {
				// Store credit card interaction details for further use
				const ccFormObject = this.form.creditCard;
				// Block submit if CVC hasn't been touched (Autofill)
				if (this.isCreditCard && ccFormObject && !ccFormObject.cvcTouched) {
					this.utils.hideLoader();
					return false;
				}
				// Apply or remove customer billing details
				try {
					// Reset all billing details for PayPal payment method (PayPal account details will be used)
					const billingDetails = this.isPayPalBilling ? null : this.customer.billing;
					// Force-set provider account email address
					if (this.order.isAuthenticated && billingDetails) {
						billingDetails.email = this.providerAccount.email;
					}
					// Remember customer selection upon page reload
					if (billingDetails) {
						this.rememberForm('customer', 'SET_CUSTOMER', this.customer);
						this.rememberForm('company', 'SET_COMPANY', this.isCompany);
						// Store untouched state of payment methods
						this.rememberForm('form', 'SET_FORM', {
							creditCard: null,
							phoneNumber: this.form.phoneNumber
						});
						// Apply customer billing details
						await this.utils.applyBilling(billingDetails);
					} else {
						this.forgetForm('customer');
						this.forgetForm('company');
						this.forgetForm('form');
						// Remove customer billing details
						await this.utils.removeBilling();
					}
				} catch {
					this.showPaymentFailedModal();
					return false;
				}
				// Brazilian flow - continue to the next step after applying billing
				if (this.isBrazilianFlow) {
					if (ccFormObject?.touched) {
						// Return touched state if it's already been interacted with the field
						this.$store.commit('SET_FORM', {
							creditCard: {
								touched: true
							}
						});
					}
					return true;
				}
				// Generate payment source
				try {
					// Apply form details (installments, cpj/cnpj)
					await this.utils.applyForm(this.form);
					paymentSource = await this.paymentContainer.getPaymentSource();
				} catch {
					this.showPaymentFailedModal();
					return false;
				}
				// Brazil - OMS company flow
				if (this.isBrazilianFlow && this.isCompany) {
					// Apply customer company details
					await this.utils.applyCompany({
						companyName: this.customer.billing.companyName,
						taxId: this.customer.billing.taxId,
						line1: this.customer.billing.line1,
						line2: this.customer.billing.line2
					});
					// Remember a new customerType and relevant data of tax registrations
					this.rememberForm('form', 'SET_FORM', this.form);
					// Get new payment source for updated details
					paymentSource = await this.paymentContainer.getPaymentSource();
				}
			}
			// Restore bank selection after "applyBilling" call finished
			if (this.isIDeal) {
				this.$store.commit('SET_PAYMENT', {
					...this.payment,
					bank
				});
			}
			// If customer billing form is invalid or payment source was not generated, cancel the order submit
			if (!this.formValid || !paymentSource) {
				this.focusInvalidCCField();
				this.utils.hideLoader();
				return false;
			}
			// Apply the payment method source generated for current payment selection
			try {
				await this.utils.applyPayment(paymentSource);
			} catch {
				this.showPaymentFailedModal();
				return false;
			}
			// Show the offer modal window (for company flow, the offer is triggered on the company page)
			if (this.shouldGetOffer && !this.isCompany) {
				this.utils.hideLoader();
				this.utils.showOfferModal();
				// Set all states to true, so the redirect won't be blocked by untouched CC/DirectDebit validation
				const formStatusPass = {
					completed: true,
					submitted: true,
					touched: true,
					cvcTouched: true
				};
				this.$store.commit('SET_FORM', {
					creditCard: formStatusPass,
					bancontact: formStatusPass,
					directDebit: formStatusPass
				});
				return false;
			}

			/**
			 * If the selected payment method requires redirect, continue to the payment gateway
			 * @note There are payment options (eg. Bancontact or iDeal) which will redirect on the confirm order page
			 */
			if (paymentSource.redirect) {
				return paymentSource.redirect.redirectUrl;
			}
			// Store information about Credit Card complementation status
			if (this.isCreditCard) {
				this.$store.commit('SET_FORM', {
					creditCard: {
						completed: true,
						submitted: true,
						touched: false
					}
				});
			}
			if (this.bancontact) {
				this.$store.commit('SET_FORM', {
					bancontact: {
						completed: true,
						submitted: true,
						touched: true
					}
				});
			}
			// Continue to the next checkout step
			return true;
		}
	},
	template
};
